import { useState, useEffect } from "react";
import {
  Card,
  Space,
  Select,
  Input,
  Row,
  Col,
  Tag,
  Statistic,
  DatePicker,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import { useTransportFees } from "../../../hooks/finance/useTransportFees";
import TransportFeesTable from "../../../components/finance/transport-fees/TransportFeesTable";
import TransportFeeDetailDrawer from "../../../components/finance/transport-fees/TransportFeeDetailDrawer";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * Transport Fees Management Page
 * Displays all transport fees with filters and detailed view
 */
const TransportFeesManagement = () => {
  const navigate = useNavigate();
  const {
    transportFees,
    loading,
    fetchTransportFees,
    fetchOutstandingTransportFees,
    pagination,
  } = useTransportFees();
  const { academicYears, fetchAcademicYears } = useAcademicYears();

  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedTransportFee, setSelectedTransportFee] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const [outstandingCount, setOutstandingCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    routeId: null,
    busId: null,
    classId: null,
    acadyr: null,
    status: null,
    dateRange: null,
  });

  /**
   * Initial data load
   */
  useEffect(() => {
    const initializeData = async () => {
      fetchTransportFees();
      fetchAcademicYears();
      fetchRoutes();
      fetchBuses();
      fetchClasses();
      fetchOutstandingCounts();
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetch routes
   */
  const fetchRoutes = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.transport.route,
        null,
        navigate,
      );
      if (response.ok) {
        setRoutes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  /**
   * Fetch buses
   */
  const fetchBuses = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.transport.buses,
        null,
        navigate,
      );
      if (response.ok) {
        setBuses(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching buses:", error);
    }
  };

  /**
   * Fetch school classes
   */
  const fetchClasses = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (response.ok) {
        setClasses(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  /**
   * Fetch outstanding fees count
   */
  const fetchOutstandingCounts = async () => {
    try {
      const outstanding = await fetchOutstandingTransportFees();
      setOutstandingCount(outstanding.length);
    } catch (error) {
      console.error("Error fetching outstanding counts:", error);
    }
  };

  /**
   * Handle filter changes - refetch with server-side filters if supported
   * Falls back to client-side filtering
   * Debounces search to avoid spam requests
   */
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      const queryFilters = {};
      if (filters.acadyr) queryFilters.acadyr = filters.acadyr;
      if (filters.routeId) queryFilters.route_id = filters.routeId;
      if (filters.busId) queryFilters.bus_id = filters.busId;
      if (filters.classId) queryFilters.class_id = filters.classId;
      if (filters.status) queryFilters.status = filters.status;
      if (filters.dateRange && filters.dateRange.length === 2) {
        queryFilters.date_from = filters.dateRange[0].format("YYYY-MM-DD");
        queryFilters.date_to = filters.dateRange[1].format("YYYY-MM-DD");
      }

      fetchTransportFees({ filters: queryFilters, page: 1 });
    }, 500);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    filters.acadyr,
    filters.routeId,
    filters.busId,
    filters.classId,
    filters.status,
    filters.searchText,
    filters.dateRange,
  ]);

  /**
   * Handle view details
   */
  const handleViewDetails = (transportFee) => {
    setSelectedTransportFee(transportFee);
    setDetailDrawerVisible(true);
  };

  /**
   * Close drawer
   */
  const closeDrawer = () => {
    setDetailDrawerVisible(false);
    setSelectedTransportFee(null);
  };

  /**
   * Handle pagination change
   */
  const handlePaginationChange = (page, pageSize) => {
    fetchTransportFees({
      page,
      pageSize,
      filters: {
        route_id: filters.routeId,
        bus_id: filters.busId,
        acadyr: filters.acadyr,
      },
    });
  };

  /**
   * Filter transport fees on the client side
   * Apply search text, route, status, and date range filters
   */
  const filteredTransportFees = transportFees.filter((fee) => {
    // Search filter (student name or SPC) — client-side only
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      const studentName = (fee.studentName || "").toLowerCase();
      const spc = (
        fee.studentSpc ||
        fee.spcCode ||
        fee.spc ||
        ""
      ).toLowerCase();

      if (!studentName.includes(search) && !spc.includes(search)) {
        return false;
      }
    }

    return true;
  });

  return (
    <>
      <Helmet>
        <title>Transport Fees | Finance</title>
      </Helmet>

      <div className="content-wrapper">
        <div className="mg-b-20">
          <h4 className="mg-b-5">Transport Fees Management</h4>
          <p className="tx-color-03 mg-b-0">
            Manage and view all transport fee assignments and payments
          </p>
        </div>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Transport Fees"
                value={pagination.total || 0}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Outstanding Fees"
                value={outstandingCount}
                valueStyle={{ color: "#f5222d" }}
                prefix={outstandingCount > 0 ? "⚠️" : "✓"}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          {/* Filters */}
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Input
                  placeholder="Search by student name or SPC..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Bus"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.busId}
                  onChange={(value) => setFilters({ ...filters, busId: value })}
                >
                  {Array.isArray(buses) &&
                    buses.map((bus) => (
                      <Option key={bus.id} value={bus.id}>
                        {bus.busNumber} - {bus.plateNumber}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Route"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.routeId}
                  onChange={(value) =>
                    setFilters({ ...filters, routeId: value })
                  }
                >
                  {Array.isArray(routes) &&
                    routes.map((route) => (
                      <Option key={route.id} value={route.id}>
                        {route.name}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyr}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyr: value })
                  }
                >
                  {Array.isArray(academicYears) &&
                    academicYears.map((year) => (
                      <Option key={year.id} value={year.acadyr}>
                        {year.acadyr}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Pending">Pending</Option>
                  <Option value="Partially Paid">Partially Paid</Option>
                  <Option value="Fully Paid">Fully Paid</Option>
                  <Option value="Overdue">Overdue</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Select
                  placeholder="Filter by Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.classId}
                  onChange={(value) =>
                    setFilters({ ...filters, classId: value })
                  }
                >
                  {Array.isArray(classes) &&
                    classes.map((cls) => (
                      <Option key={cls.id} value={cls.id}>
                        {cls.name}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={filters.dateRange}
                  onChange={(dates) =>
                    setFilters({ ...filters, dateRange: dates })
                  }
                  format="YYYY-MM-DD"
                  placeholder={["Start Date", "End Date"]}
                />
              </Col>
            </Row>

            {/* Results Summary */}
            {(filters.searchText ||
              filters.busId ||
              filters.routeId ||
              filters.classId ||
              filters.acadyr ||
              filters.status ||
              filters.dateRange) && (
              <div>
                <Tag color="blue">
                  Showing {filteredTransportFees.length} of{" "}
                  {transportFees.length} transport fee(s)
                </Tag>
              </div>
            )}

            {/* Transport Fees Table */}
            <TransportFeesTable
              data={filteredTransportFees}
              loading={loading}
              onView={handleViewDetails}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
            />
          </Space>
        </Card>

        {/* Transport Fee Detail Drawer */}
        <TransportFeeDetailDrawer
          visible={detailDrawerVisible}
          onClose={closeDrawer}
          transportFeeData={selectedTransportFee}
        />
      </div>
    </>
  );
};

export default TransportFeesManagement;
